import { FIXED_DT_SECONDS } from "@air-hockey/game-core";
import { clamp, lerp } from "./math";
import { predictLocalMallet } from "./malletPrediction";
import { NetworkSession, type SentInput, type ServerMessage } from "./networkSession";
import type {
  BodyState,
  MatchPhase,
  PlayerSlot,
  RenderState,
  ServerSnapshot,
  SimulationAdapter,
  SimulationEvent,
  Vector2,
} from "./types";

const TABLE_WIDTH = 1_000;
const TABLE_HEIGHT = 1_600;
const INTERPOLATION_DELAY_MS = 70;
const MAX_EXTRAPOLATION_MS = 100;
const MAX_ACKNOWLEDGED_INTENT_REPLAY_TICKS = 12;

interface TimedBody {
  receivedAt: number;
  body: BodyState;
}

export class OnlineSimulation implements SimulationAdapter {
  readonly kind = "online" as const;

  private readonly listeners = new Set<(event: SimulationEvent) => void>();
  private readonly opponentHistory: TimedBody[] = [];
  private readonly unsubscribeMessage: () => void;
  private readonly unsubscribeInputs: () => void;
  private readonly unackedInputs: SentInput[] = [];
  private phase: MatchPhase = "waiting";
  private localPredicted: BodyState = { x: 0.5, y: 0.75, vx: 0, vy: 0 };
  private localCorrection: Vector2 = { x: 0, y: 0 };
  private opponent: BodyState = { x: 0.5, y: 0.2, vx: 0, vy: 0 };
  private puck: BodyState = { x: 0.5, y: 0.5, vx: 0, vy: 0 };
  private latestPuck: TimedBody | null = null;
  private scoreLocal = 0;
  private scoreOpponent = 0;
  private remainingTimeMs: number | null = null;
  private latestSnapshotAt = 0;
  private countdown: number | null = null;
  private lastGoal: "local" | "opponent" | null = null;
  private winner: "local" | "opponent" | "draw" | null = null;
  private finishReason: string | null = null;
  private lastRenderAt = performance.now();
  private elapsedMs = 0;
  private maxPuckSpeed = 0;
  private disposed = false;
  private latestIntent: Vector2 = { x: 0.5, y: 0.75 };
  private hasLocalIntent = false;
  private localPredictionAccumulator = 0;
  private latestAcceptedTick = -1;

  constructor(
    private readonly network: NetworkSession,
    private readonly slot: PlayerSlot,
  ) {
    this.unsubscribeMessage = network.subscribeMessages((message) => this.handleMessage(message));
    this.unsubscribeInputs = network.subscribeSentInputs((input) => {
      this.unackedInputs.push({
        ...input,
        target: this.slot === "A" ? input.target : this.rotatePoint(input.target),
      });
      if (this.unackedInputs.length > 120) this.unackedInputs.shift();
    });
  }

  getState(now = performance.now()): RenderState {
    const renderDeltaMs = clamp(now - this.lastRenderAt, 0, 50);
    this.lastRenderAt = now;
    if (this.phase === "playing") this.elapsedMs += renderDeltaMs;

    const correctionMagnitude = Math.hypot(
      this.localCorrection.x * TABLE_WIDTH,
      this.localCorrection.y * TABLE_HEIGHT,
    );
    const correctionTimeConstantMs = lerp(95, 24, clamp(correctionMagnitude / 180, 0, 1));
    const correctionDecay = Math.exp(-renderDeltaMs / correctionTimeConstantMs);
    this.localCorrection.x *= correctionDecay;
    this.localCorrection.y *= correctionDecay;

    const opponent = this.interpolateOpponent(now - INTERPOLATION_DELAY_MS);
    const puckTarget = this.extrapolatedPuck(now);
    const puckCorrection = this.latestPuck === null ? 1 : 1 - Math.exp(-renderDeltaMs / 48);
    this.puck = {
      x: lerp(this.puck.x, puckTarget.x, puckCorrection),
      y: lerp(this.puck.y, puckTarget.y, puckCorrection),
      vx: puckTarget.vx,
      vy: puckTarget.vy,
    };

    const timerDrift = this.phase === "playing" && this.remainingTimeMs !== null ? Math.max(0, now - this.latestSnapshotAt) : 0;
    return {
      phase: this.phase,
      local: {
        ...this.localPredicted,
        x: clamp(this.localPredicted.x + this.localCorrection.x, 0.1, 0.9),
        y: clamp(this.localPredicted.y + this.localCorrection.y, 0.52, 0.94),
      },
      opponent,
      puck: this.puck,
      scoreLocal: this.scoreLocal,
      scoreOpponent: this.scoreOpponent,
      remainingTimeMs: this.remainingTimeMs === null ? null : Math.max(0, this.remainingTimeMs - timerDrift),
      countdown: this.countdown,
      lastGoal: this.lastGoal,
      winner: this.winner,
      finishReason: this.finishReason,
      stats: {
        touches: 0,
        maxPuckSpeed: this.maxPuckSpeed,
        elapsedMs: this.elapsedMs,
      },
    };
  }

  setLocalTarget(target: Vector2): void {
    const localTarget = {
      x: clamp(target.x, 0.115, 0.885),
      y: clamp(target.y, 0.55, 0.93),
    };
    this.latestIntent = localTarget;
    this.hasLocalIntent = true;

    if (this.phase === "playing") {
      // Reflect pointer intent before the next animation step. The shared
      // predictor applies the same bounds and movement rules as the server.
      this.localPredicted = predictLocalMallet(this.localPredicted, [{ target: localTarget }]);
      this.localCorrection = { x: 0, y: 0 };
      this.localPredictionAccumulator = 0;
    }

    const serverTarget = this.slot === "A" ? localTarget : this.rotatePoint(localTarget);
    this.network.sendInput(serverTarget);
  }

  step(deltaSeconds: number): void {
    if (
      this.disposed ||
      !this.hasLocalIntent ||
      this.phase !== "playing"
    ) {
      return;
    }
    this.localPredictionAccumulator += clamp(deltaSeconds, 0, 0.05);
    while (this.localPredictionAccumulator >= FIXED_DT_SECONDS) {
      this.localPredicted = predictLocalMallet(this.localPredicted, [{ target: this.latestIntent }]);
      this.localPredictionAccumulator -= FIXED_DT_SECONDS;
    }
  }

  subscribe(listener: (event: SimulationEvent) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.unsubscribeMessage();
    this.unsubscribeInputs();
    this.listeners.clear();
    this.opponentHistory.length = 0;
  }

  private handleMessage(message: ServerMessage): void {
    if (message.type === "SNAPSHOT") {
      this.applySnapshot(message);
      return;
    }

    if (message.type === "MATCH_STATE" && message.state && typeof message.state === "object") {
      const state = message.state as Record<string, unknown>;
      if (state.type === "SNAPSHOT") this.applySnapshot(state as unknown as ServerSnapshot);
      else if (typeof state.phase === "string") this.setPhase(state.phase as MatchPhase);
      return;
    }

    if (message.type === "COUNTDOWN") {
      const value = typeof message.remainingTicks === "number" ? message.remainingTicks / 60 : null;
      this.countdown = value === null ? null : Math.max(1, Math.ceil(value));
      this.setPhase("countdown");
      return;
    }

    if (message.type === "GOAL") {
      const scorer = this.isLocalServerSlot(message.scorer) ? "local" : "opponent";
      this.lastGoal = scorer;
      if (typeof message.scoreA === "number" && typeof message.scoreB === "number") {
        this.assignScores(message.scoreA, message.scoreB);
      }
      this.puck = { x: 0.5, y: 0.5, vx: 0, vy: 0 };
      this.latestPuck = { receivedAt: performance.now(), body: this.puck };
      this.setPhase("goal");
      this.emit({ type: "goal", scorer });
      return;
    }

    if (message.type === "FINISHED") {
      if (typeof message.scoreA === "number" && typeof message.scoreB === "number") {
        this.assignScores(message.scoreA, message.scoreB);
      }
      this.setPhase("finished");
      const winner = message.winner == null ? "draw" : this.isLocalServerSlot(message.winner) ? "local" : "opponent";
      this.winner = winner;
      this.finishReason = typeof message.reason === "string" ? message.reason : null;
      this.emit({ type: "finished", winner });
      return;
    }

    if (message.type === "WELCOME") {
      this.setPhase("ready");
      if (this.hasLocalIntent) {
        this.network.sendInput(this.slot === "A" ? this.latestIntent : this.rotatePoint(this.latestIntent));
      }
    }
  }

  private applySnapshot(snapshot: ServerSnapshot): void {
    if (!Number.isFinite(snapshot.tick) || snapshot.tick <= this.latestAcceptedTick) return;
    this.latestAcceptedTick = snapshot.tick;
    const now = performance.now();
    const previousVisual = {
      x: this.localPredicted.x + this.localCorrection.x,
      y: this.localPredicted.y + this.localCorrection.y,
    };
    const serverA = this.normalizeWireBody(snapshot.playerA);
    const serverB = this.normalizeWireBody(snapshot.playerB);
    const serverPuck = this.normalizeWireBody(snapshot.puck);
    const localAuth = this.slot === "A" ? serverA : this.rotateBody(serverB);
    const opponentAuth = this.slot === "A" ? serverB : this.rotateBody(serverA);
    const orientedPuck = this.slot === "A" ? serverPuck : this.rotateBody(serverPuck);

    while (this.unackedInputs[0] && this.unackedInputs[0].sequence <= snapshot.ackSequence) {
      this.unackedInputs.shift();
    }
    if (snapshot.phase === "playing") {
      let replayedLocal = predictLocalMallet(localAuth, this.unackedInputs);
      if (this.hasLocalIntent) {
        replayedLocal = this.replayAcknowledgedIntentTowardVisual(replayedLocal, previousVisual);
      }
      this.localPredicted = replayedLocal;
      this.localCorrection = {
        x: previousVisual.x - replayedLocal.x,
        y: previousVisual.y - replayedLocal.y,
      };
    } else {
      this.localPredicted = localAuth;
      this.localCorrection = { x: 0, y: 0 };
      this.localPredictionAccumulator = 0;
    }
    if (
      this.hasLocalIntent &&
      this.unackedInputs.length === 0 &&
      Math.hypot(
        (localAuth.x - this.latestIntent.x) * TABLE_WIDTH,
        (localAuth.y - this.latestIntent.y) * TABLE_HEIGHT,
      ) <= 4
    ) {
      this.hasLocalIntent = false;
      this.localPredictionAccumulator = 0;
    }

    this.opponentHistory.push({ receivedAt: now, body: opponentAuth });
    if (this.opponentHistory.length > 20) this.opponentHistory.shift();
    this.latestPuck = { receivedAt: now, body: orientedPuck };
    if (this.latestSnapshotAt === 0) this.puck = orientedPuck;
    this.latestSnapshotAt = now;
    this.remainingTimeMs = snapshot.remainingTimeMs;
    this.assignScores(snapshot.scoreA, snapshot.scoreB);
    this.maxPuckSpeed = Math.max(
      this.maxPuckSpeed,
      Math.hypot(orientedPuck.vx * TABLE_WIDTH, orientedPuck.vy * TABLE_HEIGHT),
    );
    this.countdown = snapshot.phase === "countdown" ? this.countdown : null;
    this.setPhase(snapshot.phase);
  }

  private interpolateOpponent(renderAt: number): BodyState {
    if (this.opponentHistory.length === 0) return this.opponent;
    let before = this.opponentHistory[0];
    let after = this.opponentHistory[this.opponentHistory.length - 1];
    if (!before || !after) return this.opponent;

    for (let index = 1; index < this.opponentHistory.length; index += 1) {
      const candidate = this.opponentHistory[index];
      if (!candidate) continue;
      if (candidate.receivedAt >= renderAt) {
        after = candidate;
        before = this.opponentHistory[index - 1] ?? candidate;
        break;
      }
    }

    const duration = Math.max(1, after.receivedAt - before.receivedAt);
    const amount = clamp((renderAt - before.receivedAt) / duration, 0, 1);
    this.opponent = {
      x: lerp(before.body.x, after.body.x, amount),
      y: lerp(before.body.y, after.body.y, amount),
      vx: lerp(before.body.vx, after.body.vx, amount),
      vy: lerp(before.body.vy, after.body.vy, amount),
    };
    return this.opponent;
  }

  private replayAcknowledgedIntentTowardVisual(base: BodyState, previousVisual: Vector2): BodyState {
    let current = base;
    let best = base;
    let bestDistance = this.logicalDistance(base, previousVisual);
    for (let tick = 0; tick < MAX_ACKNOWLEDGED_INTENT_REPLAY_TICKS; tick += 1) {
      current = predictLocalMallet(current, [{ target: this.latestIntent }]);
      const distance = this.logicalDistance(current, previousVisual);
      if (distance <= bestDistance) {
        best = current;
        bestDistance = distance;
      } else if (tick > 0) {
        break;
      }
    }
    return best;
  }

  private logicalDistance(from: Vector2, to: Vector2): number {
    return Math.hypot((from.x - to.x) * TABLE_WIDTH, (from.y - to.y) * TABLE_HEIGHT);
  }

  private extrapolatedPuck(now: number): BodyState {
    if (!this.latestPuck) return this.puck;
    const elapsedSeconds = clamp(now - this.latestPuck.receivedAt, 0, MAX_EXTRAPOLATION_MS) / 1_000;
    return {
      x: clamp(this.latestPuck.body.x + this.latestPuck.body.vx * elapsedSeconds, -0.05, 1.05),
      y: clamp(this.latestPuck.body.y + this.latestPuck.body.vy * elapsedSeconds, -0.05, 1.05),
      vx: this.latestPuck.body.vx,
      vy: this.latestPuck.body.vy,
    };
  }

  private normalizeWireBody(body: BodyState): BodyState {
    const logical = Math.abs(body.x) > 1.5 || Math.abs(body.y) > 1.5;
    return logical
      ? {
          x: body.x / TABLE_WIDTH,
          y: body.y / TABLE_HEIGHT,
          vx: body.vx / TABLE_WIDTH,
          vy: body.vy / TABLE_HEIGHT,
        }
      : body;
  }

  private rotateBody(body: BodyState): BodyState {
    return {
      x: 1 - body.x,
      y: 1 - body.y,
      vx: -body.vx,
      vy: -body.vy,
    };
  }

  private rotatePoint(point: Vector2): Vector2 {
    return { x: 1 - point.x, y: 1 - point.y };
  }

  private isLocalServerSlot(slot: "PLAYER_A" | "PLAYER_B" | undefined): boolean {
    return slot === (this.slot === "A" ? "PLAYER_A" : "PLAYER_B");
  }

  private assignScores(scoreA: number, scoreB: number): void {
    this.scoreLocal = this.slot === "A" ? scoreA : scoreB;
    this.scoreOpponent = this.slot === "A" ? scoreB : scoreA;
  }

  private setPhase(phase: MatchPhase): void {
    if (this.phase === phase) return;
    this.phase = phase;
    if (phase === "playing") {
      this.countdown = null;
      this.lastGoal = null;
    }
    this.emit({ type: "phase", phase });
  }

  private emit(event: SimulationEvent): void {
    this.listeners.forEach((listener) => listener(event));
  }
}
